/**
 * Shared trimming for the in-browser diagnostic rings (`debug-ring`, `filter-leak-diag`).
 *
 * The rings are capped by ENTRY COUNT, which bounds nothing useful: a single entry carries whatever
 * payload its call site passed (a filter tree, a pagination object), so N entries can be bytes or
 * hundreds of kilobytes. They live in the same storage areas the app persists real state into, and
 * a diagnostic that helps fill the quota it is meant to help diagnose is worse than no diagnostic.
 *
 * So the count cap gains a CHARACTER cap: oldest entries are dropped until the serialized ring fits.
 * Oldest-first because the newest entry is the one being written and the one closest to whatever is
 * being investigated.
 */

/**
 * Drop entries from the front until the ring serializes within `maxChars`.
 *
 * Measures each entry ONCE and grows a suffix from the newest end, rather than re-serializing the
 * whole ring after every drop. The callers are hot -- `dbg()` runs on every navigation and
 * `logFilterDiag()` from a reducer and the HTTP interceptor -- and a ring that sits over budget
 * would otherwise pay one full `JSON.stringify` per dropped entry, on every single call.
 *
 * The newest entry is always kept, whatever its size: it is the one being written and the one
 * closest to whatever is being investigated. An entry that cannot be serialized at all (a cycle)
 * measures as `Infinity`, so nothing older than it is ever carried along with it.
 */
export function trimRingToBudget<T>(entries: T[], maxChars: number): T[] {
  if (entries.length <= 1) {
    return entries;
  }
  const lengths = entries.map(entryLength);
  // `[a,b,c]` is 2 brackets + every entry + one comma between each adjacent pair.
  let start = lengths.length - 1;
  let total = 2 + lengths[start];
  while (start > 0) {
    const withOlder = total + lengths[start - 1] + 1;
    if (withOlder > maxChars) {
      break;
    }
    total = withOlder;
    start--;
  }
  return start === 0 ? entries : entries.slice(start);
}

/**
 * Serialized size of one entry as it appears inside the array, or `Infinity` when it cannot be
 * measured. A value with no JSON representation (a function, a symbol, `undefined`) is written as
 * `null` by `JSON.stringify` when it sits in an array, which is 4 characters.
 */
function entryLength(entry: unknown): number {
  try {
    const raw = JSON.stringify(entry);
    return raw === undefined ? 4 : raw.length;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}
